import { Injectable } from '@angular/core';
import { enablePortletScript, FilterSchema, PlThemeColors } from '@gspwidget/util';
import {
  bgPropertyPreset,
  clickEventPropertyPreset,
  datalessJumpOption, frontendFilterProp, PropertyColorOption, PropertyOption,
  PropertyTypes, RegisteredWidgetConfig, WidgetConfig, WidgetPropertyMap, WidgetRegistry
} from '@gspwidget/widget-devkit';
import { isWidgetRuntimeAggActive } from './widget/runtime-agg';

const PropertyTypeDefaultValues = {
  [PropertyTypes.Text]: '',
  [PropertyTypes.Bool]: false,
  [PropertyTypes.Number]: 0,
  [PropertyTypes.DataField]: {}
  // [PropertyTypes.Color]: '#000000',
  // [PropertyTypes.Enum]: 0,
  // [PropertyTypes.Date]: new Date()
  // [PropertyTypes.Object]: {},
}

const titleProp = {
  name: 'title',
  displayName: 'portletTitle',
}
const commonTitleBarProps: PropertyOption[] = [{
  category: 'titleBar',
  name: 'titleBarShowRefresh',
  displayName: 'showRefreshBtn',
  type: PropertyTypes.Bool,
  // boolOption: { default: true },
  ifShow (v) { return !v.titleBarHide },
}, {
  ...datalessJumpOption,
  category: 'titleBar',
  name: 'titleBarJump',
  userCustom: true,
  ifShow (v) { return !v.titleBarHide },
}, {
  name: 'extendBtns',
  category: 'titleBar',
  isArray: true,
  type: PropertyTypes.Object,
  objectOption: {
    objPropertyOptions: [, {
      name: 'contentType',
      type: PropertyTypes.Enum,
      enumOption: {
        items: ['text', 'materialIcons']
      }
    }, {
      name: 'content',
      ifShow (pvs) { return pvs.contentType === 'text' }
    }, {
      name: 'icon',
      textOption: { i18n: false },
      ifShow (pvs) { return pvs.contentType === 'materialIcons' }
    }, {
      name: 'iconSize',
      type: PropertyTypes.Number,
      numberOption: { default: 20 },
      ifShow (pvs) { return pvs.contentType === 'materialIcons' }
    }, {
    // TODO: 操作绑定后续放开
    //   name: 'operation',
    //   help2Option: {
    //     preset: 'rtfOperation'
    //   }
    // }, {
      ...clickEventPropertyPreset,
      name: 'clickEvent',
    // }, {
    //   ...datalessJumpOption,
    //   name: 'jump',
    }]
  },
  ifShow (pvs) { return !pvs.titleBarHide },
}]
/**
 * 用此方法获取元部件的公共标题配置项，搞成动态生成原因是默认值真假不一定，要根据 widgetConfig.enableCommonTitleBar
 * @param titleBarHideDefault 
 * @returns 
 */
function getTitleBarProps (titleBarHideDefault = false) {
  return [
    {
      category: 'titleBar',
      name: 'titleBarHide',
      type: PropertyTypes.Bool,
      userCustom: true,
      boolOption: {
        default: titleBarHideDefault
      }
    },
    ...commonTitleBarProps,
  ]
}

export const SHOW_RUNTIME_AGG_PROP_NAME = 'showRuntimeAggBtn'
export const runtimeAggProps: PropertyOption[] = [{
  category: 'titleBar',
  name: SHOW_RUNTIME_AGG_PROP_NAME,
  type: PropertyTypes.Bool,
  boolOption: { default: false },
}]

/** 所有部件都有的样式配置项 */
export const COMMON_STYLE_PROP_NAME = 'CssStyle'
export function getPortletStyleProps (widgetConfig: WidgetConfig): PropertyOption[] {
  return [{
    ...bgPropertyPreset,
    category: 'style',
    name: 'bg',
    displayName: 'portletBg',
    tip: 'portletBgTip',
  }, {
    category: 'style',
    name: COMMON_STYLE_PROP_NAME,
    type: PropertyTypes.Code,
    codeOption: { lang: 'css' },
  }, {
    category: 'style',
    name: 'wcsEnabled',
    type: PropertyTypes.Bool,
  }, {
    category: 'style',
    name: 'wcsBorderRadius',
    type: PropertyTypes.Number,
    ifShow (pvs) { return pvs.wcsEnabled }
  }, 
  ...(
    widgetConfig.enableWidgetCommonStyleBg
      ? [{
        category: 'style',
        name: 'wcsBgEnabled',
        displayName: 'enableCustomTextColor',
        type: PropertyTypes.Bool,
        ifShow (pvs) { return pvs.wcsEnabled }
      }, {
        category: 'style',
        name: 'wcsBgColor',
        type: PropertyTypes.Color,
        // 跟前面的部件背景色专用配置重复, 故隐藏此项
        ifShow () { return false }
      }, {
        category: 'style',
        name: 'textColor',
        tip: '兜底文本色，部件内已设置颜色的文本不受此值影响，仅有未设置颜色的文本会继承此颜色。此属性不影响部件公共标题。',
        type: PropertyTypes.Color,
        colorOption: {
          default: '#000000d9'
        },
        ifShow (pvs) { return pvs.wcsEnabled && pvs.wcsBgEnabled }
      }]
      : []
  ), {
    category: 'style',
    name: 'wcsEnableShadow',
    type: PropertyTypes.Bool,
    ifShow (pvs) { return pvs.wcsEnabled }
  }, {
    category: 'style',
    name: 'wcsPadding',
    type: PropertyTypes.Number,
    ifShow (pvs) { return pvs.wcsEnabled }
  }]
}

/**
 * 色系
 * 对于部件，色系就是主题，就是 theme
 */
const themeProps: PropertyOption[] = [{
  group: 'style',
  category: 'style',
  displayName: '部件色系',
  type: PropertyTypes.Enum,
  enumOption: {
    items: [
      'inherit',
      'manuallyChoose',
      'customize',
    ]
  },
  name: 'themeStrategy',
}, {
  group: 'style',
  category: 'style',
  displayName: '选择色系',
  type: PropertyTypes.Theme,
  name: 'theme',
  ifShow (pvs) { return pvs.themeStrategy === 'manuallyChoose' },
}]

const datasetProps: PropertyOption[] = [{
  group: 'advanced',
  category: 'data',
  name: 'autoRefreshData',
  type: PropertyTypes.Bool,
}, {
  group: 'advanced',
  category: 'data',
  name: 'refreshInterval',
  tip: '',
  type: PropertyTypes.Number,
  needReboot: true,
  ifShow (pvs) { return pvs.autoRefreshData },
}, {
  group: 'advanced',
  category: 'data',
  name: 'refreshWhenTabShow',
  type: PropertyTypes.Bool,
}]

/**
 * name 被 widget Comp 引用了
 */
export const jsBeforeLoadProp: PropertyOption = {
  group: 'advanced',
  category: 'script',
  name: 'jsBeforeLoad',
  // displayName: '加载前运行JS',
  type: PropertyTypes.Code,
  codeOption: {
    lang: 'javascript'
  }
}
/**
 * name 被 widget Comp 引用了
 */
export const jsOnBootProp: PropertyOption = {
  group: 'advanced',
  category: 'script',
  name: 'jsOnBoot',
  // displayName: '启动时运行JS',
  type: PropertyTypes.Code,
  codeOption: {
    lang: 'javascript'
  }
}


/** echarts option 处理器函数列表 */
export const ECHARTS_OPTION_PROCESSORS = 'ECHARTS_OPTION_PROCESSORS'
export interface OptionProcessorMap {
  [key: string]: (option) => void
}
/**
 * 2510 的新版图表选项处理, 此 prop 会完全覆盖图表部件里的旧版
 */
const optionProcessorName2: PropertyOption = {
  type: PropertyTypes.Help2,
  displayName: '图表选项处理',
  help2Option: {
    parts: [{
      xcGetData: () => Object.keys(window[ECHARTS_OPTION_PROCESSORS] || {})
        .map(processorKey => ({ processorKey })),
      xcCols: ['processorKey'],
      xcCode: 'processorKey',
    }],
    valueField: 'processorKey',
    labelField: 'processorKey'
  }
}

// const testProps: PropertyOption[] = []
// const testProps: PropertyOption[] = [{
//   ...bgPropertyPreset,
//   name: 'testColor',
// }]
// const testProps: PropertyOption[] = [{
//   name: 'test',
//   type: PropertyTypes.Help2,
//   help2Option: {
//     preset: 'rtfFuncsWithParentsChildren'
//   }
// }]

@Injectable({
  providedIn: 'root'
})
export class WidgetPropertyService {

  static DynamicPropsMap = {}
  public static RegisterProperties (name: string, props: PropertyOption[]) {
    WidgetPropertyService.DynamicPropsMap[name] = props
  }
  public static GetRegisteredProperties (name: string) {
    return WidgetPropertyService.DynamicPropsMap[name]
  }

  constructor (
  ) { }
  /**
   * 获得某种类型的全局默认值，在完全没有值时使用
   * @param prop
   * @param arrayItem 是否是来获取 array 的子项默认值的，默认不是。
   * 如果不是，若 prop.isArray 为真，就返回空数组 []；
   * 如果是，则不管 prop.isArray 是否为真，总返回 prop.type 对应的默认值，仅应用于增加数组项时获取新项。
   */
  public getPropertyDefaultValue (prop: PropertyOption, arrayItem = false, colorIndex?: number) {
    if (!arrayItem && prop.isArray) return []
    // object: 递归生成具有所有 key 的对象，每个 key 的 value 是默认值
    if (prop.type === PropertyTypes.Object) {
      const newObj = {}
      prop.objectOption.objPropertyOptions.forEach((childProp: PropertyOption) => {
        newObj[childProp.name] = (childProp[childProp.type]
          && childProp[childProp.type].default)
          || this.getPropertyDefaultValue(childProp,arrayItem,colorIndex)
      })
      return newObj
    } else if (prop.type === PropertyTypes.Enum) {
      if (!prop.enumOption || !prop.enumOption.items || !prop.enumOption.items[0]) return
      const firstItem = prop.enumOption.items[0]
      return typeof firstItem === 'string'
        ? firstItem
        : firstItem['value']
    } else if (prop.type === PropertyTypes.Color) {
      const colors = this.getColorsByColorOption(prop.colorOption)
      // const i = Math.random()
      // return colors[Math.floor(i * colors.length)]
      // return colors[0]
       // 循环取值：当 colorIndex 大于颜色数组长度时，循环使用
      const index = colorIndex !== undefined 
        ? colorIndex % colors.length 
        : 0
      return colors[index]
    } else if (prop.type === PropertyTypes.Filter) {
      return new FilterSchema()
    } else {
      return PropertyTypeDefaultValues[prop.type]
    }
  }
  /**
   * 元部件加载、编译完成后，组装新的 properties
   * widget name
   * -> constructor name
   * -> widget + title bar + base properties
   * -> 元部件的所有可配置 properties
   */
  public getWidgetProps (widgetName: string, widgetConfig: RegisteredWidgetConfig): PropertyOption[] {
    // widget 子类中的 property
    // TODO: 这里不应该出现直接从 map 里取，应该封装起来。map 是这套东西的具体实现，相关操作实现应该统一封装到底层 decrators、registry 里面
    const widgetPropsOriginal = WidgetPropertyMap.get(widgetConfig.ctor) || []
    const widgetProps = this.processWidgetProps(widgetPropsOriginal)

    // 组装
    return [
      titleProp,

      // 数据集相关
      ...(widgetConfig.enableDataConfig
        ? datasetProps
        : []),
      
        // 运行时聚合开关
      ...(isWidgetRuntimeAggActive(widgetName)
        ? runtimeAggProps
        : []),
      
        // 标题配置
      ...getTitleBarProps(!widgetConfig.enableCommonTitleBar),
      
      // 筛选配置
      ...(widgetConfig.enableDataConfig
        ? [frontendFilterProp]
        : []),
      
        // 元部件自身配置
      ...widgetProps,
      
      // ...testProps,
      ...getPortletStyleProps(widgetConfig),
      ...(enablePortletScript
        ? [jsBeforeLoadProp, jsOnBootProp]
        : [])
    ]
  }
  /**
   * mixin 已有pv + 元部件推荐的默认pv + 此类型的全局默认pv
   */
  public mixinWidgetPropValues (props: PropertyOption[], propValues: object) {
    const mixinedPropValues: { [propName: string]: any } = { ...propValues }
    // 检查所有 properties，哪个没有值，就赋上默认推荐值
    props.forEach(prop => {
      // 这里必须是判断 undefined，否则 bool 类型的 false 也可能判定为无值
      if (mixinedPropValues[prop.name] === undefined) {
        mixinedPropValues[prop.name] = prop[prop.type] && prop[prop.type].default
          // 如果元部件无默认值，就加入全局的当前类型默认值
          || this.getPropertyDefaultValue(prop)
      }
    })
    return mixinedPropValues
  }

  public getColorsByColorOption (colorOption: PropertyColorOption) {
    if (colorOption && colorOption.recommendPalette) return colorOption.recommendPalette
    const colorType = colorOption ? colorOption.type : 'pure'
    let colors
    switch (colorType) {
      case 'gradient':
        colors = PlThemeColors.gradient
        break
      case 'pureOrGradient':
        colors = [...PlThemeColors.pure, ...PlThemeColors.gradient]
        break
      case 'pure':
      default:
        colors = PlThemeColors.pure
        break
    }
    return colors
  }

  /**
   * 加工元部件 props, 加工完的版本必须返回, 可以不改变引用
   * 2510 增加此机制, 暂时仅用于批量改变图表元部件的“图表选项处理”prop实现
   * 有了这个, 就不用改、打包、发布、测所有涉及的元部件了
   * @param props 元部件原始的 props
   */
  private processWidgetProps (props: PropertyOption[]) {
    const optionProcessorNameProp = props.find(prop => prop.name === 'optionProcessorName')
    if (optionProcessorNameProp) {
      Object.assign(optionProcessorNameProp, optionProcessorName2)
    }
    return props
  }
}
